<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { Size } from '../types/size';
import Icon from './Icon.vue';

interface Props {
	/** Stable `data-testid` for E2E selectors. Rendered on the interactive element, not the wrapper. */
	testId?: string;
	label?: string;
	icon?: string;
	iconPosition?: 'start' | 'end';
	isPrimary?: boolean;
	isBold?: boolean;
	size?: Extract<Size, 'sm' | 'md'>;
	to?: RouteLocationRaw;
	target?: string;
	external?: boolean;
	loading?: boolean;
	type?: 'button' | 'submit' | 'reset';
	disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	type: 'button',
	iconPosition: 'end',
});

defineEmits<{
	click: [MouseEvent];
}>();
</script>

<template>
	<NuxtLink
		class="btn-link"
		:class="[{
			[`btn-link--${props.size}`]: props.size,
			'btn-link--primary': props.isPrimary,
			'btn-link--bold': props.isBold,
			'btn-link--loading': props.loading,
		}]"
		:to="props.to"
		:external="external"
		:target="props.target"
		:rel="props.target ? 'noopener noreferrer' : null"
		:data-testid="props.testId"
		v-bind="$attrs"
		v-if="to && !props.disabled"
	>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'start'" />
		<span class="btn__text" v-if="props.label">
			{{ props.label }}
		</span>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'end'" />
		<span class="btn__loading" v-if="props.loading"></span>
	</NuxtLink>

	<button
		class="btn-link"
		:class="[{
			[`btn-link--${props.size}`]: props.size,
			'btn-link--primary': props.isPrimary,
			'btn-link--bold': props.isBold,
			'btn-link--loading': props.loading,
		}]"
		:data-testid="props.testId"
		v-bind="$attrs"
		:type="type"
		:disabled="props.disabled || props.loading"
		v-else
		@click="$emit('click', $event)"
	>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'start'" />
		<span class="btn__text" v-if="props.label">
			{{ props.label }}
		</span>
		<Icon :name="props.icon" v-if="props.icon && props.iconPosition === 'end'" />
		<span class="btn__loading" v-if="props.loading"></span>
	</button>
</template>
